import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import Header from "./components/Header";
import CommandPalette from "./command/CommandPalette";
import OracleAlertsHost from "./command/OracleAlertsHost";
import Footer from "./components/Footer";
import { ScrollManager } from "./components/ScrollManager";
import Home from "./pages/HomePage";
import Archive from "./pages/Archive";
import Connections from "./pages/Connections";
import Spotlight from "./pages/Spotlight";
import TopicDetail from "./pages/TopicDetail";
import Submit from "./pages/Submit";
import Status from "./pages/Status";
import About from "./pages/About";
import Faq from "./pages/Faq";
import EditorialStandards from "./pages/EditorialStandards";
import Legal from "./pages/Legal";
import Unsubscribe from "./pages/Unsubscribe";
import Trail from "./pages/Trail";
import Inbox from "./pages/Inbox";
import EpsteinFiles from "./pages/EpsteinFiles";
import EpsteinEntity from "./pages/EpsteinEntity";
import EpsteinFlightLogs from "./pages/EpsteinFlightLogs";
import EpsteinSource from "./pages/EpsteinSource";
import EpsteinEvidence from "./pages/EpsteinEvidence";
import EpsteinTimeline from "./pages/EpsteinTimeline";
import EpsteinDossier from "./pages/EpsteinDossier";
import Admin from "./pages/Admin";
import AdminLogin from "./pages/AdminLogin";
import AuthCallback from "./pages/AuthCallback";
import Jericho from "./pages/Jericho";
import Membership from "./pages/Membership";
import OperativeCardPage from "./pages/OperativeCardPage";
import PublicOperativeProfile from "./pages/PublicOperativeProfile";
import LoginPage from "./pages/auth/LoginPage";
import SignupPage from "./pages/auth/SignupPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import GoogleAuthCallback from "./pages/auth/GoogleAuthCallback";
import MagicLinkConsumePage from "./pages/auth/MagicLinkConsumePage";
import FinishProfilePage from "./pages/auth/FinishProfilePage";
import AccessGrantedPage from "./pages/auth/AccessGrantedPage";
import MemberSecurityPage from "./pages/members/MemberSecurityPage";
import ChangePasswordPage from "./pages/members/ChangePasswordPage";
import { PersonPage, OrganizationPage, OperationPage } from "./pages/EntityPage";
import { JerichoProvider } from "./components/jericho/JerichoEasterEgg";
import { JerichoGuard } from "./components/jericho/JerichoGuard";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import { LiveAudienceTracker } from "./components/LiveAudienceTracker";
import { ArchiveStatusNotice } from "./components/ArchiveStatusNotice";
import { AuthProvider } from "./context/AuthContext";
import { InvestigationTracker } from "./investigation/InvestigationTracker";
import { InvestigationBar } from "./investigation/InvestigationBar";

const TOAST_OPTIONS = {
  style: {
    background: "#131313",
    color: "#E4DFD3",
    border: "1px solid #2D2A26",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: "12px",
    borderRadius: 0,
  },
};

// CRITICAL: detect session_id in URL fragment during render (NOT in useEffect)
// so the OAuth handoff happens before any other route runs.
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // The /admin/* tree is rendered without the public Header/Footer chrome
  // so the admin console is visually distinct from the public site and
  // can never silently fall through to looking like the homepage.
  const onAdmin = location.pathname.startsWith("/admin");
  if (onAdmin) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/*" element={<Admin />} />
      </Routes>
    );
  }

  return (
    <JerichoProvider>
      <ArchiveStatusNotice />
      <Header />
      <main className="relative z-10">
        <InvestigationBar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/archive" element={<Archive />} />
          <Route path="/connections" element={<Connections />} />
          <Route path="/connections/:slug" element={<Connections />} />
          <Route path="/spotlight" element={<Spotlight />} />
          <Route path="/topic/:id" element={<TopicDetail />} />
          {/* ── Oracle Knowledge Graph — Entity Pages ─────────────────
              /people/:slug         → PersonPage
              /organizations/:slug  → OrganizationPage
              /operations/:slug     → OperationPage
              These pages read from /api/entities/{kind}/{slug} which is
              rebuilt by scripts/build_entity_graph.py. If a slug isn't
              found, the page renders a graceful "not on file" state. */}
          <Route path="/people/:slug"        element={<PersonPage />} />
          <Route path="/organizations/:slug" element={<OrganizationPage />} />
          <Route path="/operations/:slug"    element={<OperationPage />} />
          <Route path="/epstein-files" element={<EpsteinFiles />} />
          <Route path="/epstein-files/flight-logs" element={<EpsteinFlightLogs />} />
          <Route path="/epstein-files/entity/:slug" element={<EpsteinEntity />} />
          <Route path="/epstein-files/source/:id" element={<EpsteinSource />} />
          <Route path="/epstein-files/evidence/:slug" element={<EpsteinEvidence />} />
          <Route path="/epstein-files/timeline/:slug" element={<EpsteinTimeline />} />
          <Route path="/epstein-files/places/:slug" element={<EpsteinDossier />} />
          <Route path="/submit" element={<Submit />} />
          <Route path="/status" element={<Status />} />
          <Route path="/status/:id" element={<Status />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/editorial-standards" element={<EditorialStandards />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/trail/:tid" element={<Trail />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/oracle" element={<Navigate to="/members" replace />} />
          <Route path="/inbox/:threadId" element={<Inbox />} />
          <Route path="/jericho" element={<JerichoGuard><Jericho /></JerichoGuard>} />
          <Route path="/members" element={<Membership />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/members/security" element={<MemberSecurityPage />} />
          <Route path="/members/change-password" element={<ChangePasswordPage />} />
          <Route path="/operative-card" element={<OperativeCardPage />} />
          <Route path="/member/:callsign" element={<PublicOperativeProfile />} />
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/signup" element={<SignupPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset" element={<ResetPasswordPage />} />
          <Route path="/auth/set-password" element={<ResetPasswordPage />} />
          <Route path="/auth/verify" element={<VerifyEmailPage />} />
          <Route path="/auth/callback/google" element={<GoogleAuthCallback mode="signin" />} />
          <Route path="/auth/link/google" element={<GoogleAuthCallback mode="link" />} />
          <Route path="/auth/magic-link/consume" element={<MagicLinkConsumePage />} />
          <Route path="/auth/finish-profile" element={<FinishProfilePage />} />
          <Route path="/auth/access-granted" element={<AccessGrantedPage />} />

          {/* Legacy / external-link redirects — these paths were referenced
              externally or in older copy. Redirect to the canonical surface
              so visitors never land on a blank page. */}
          <Route path="/operation-ajax" element={<Navigate to="/topic/operation-ajax-1953-iran-coup" replace />} />
          <Route path="/zorro-ranch" element={<Navigate to="/epstein-files/places/zorro-ranch" replace />} />
          <Route path="/unsubscribe-success" element={<Navigate to="/unsubscribe?status=success" replace />} />

          {/* Final catch-all so any unknown path lands on /archive instead of
              a layout-shell with an empty <main>. */}
          <Route path="*" element={<Navigate to="/archive" replace />} />
        </Routes>
      </main>
      <Footer />
    </JerichoProvider>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#0F0F0F] text-[#E4DFD3]">
      <BrowserRouter>
        <AuthProvider>
          <ScrollManager />
          <InvestigationTracker />
          <AnalyticsTracker />
          <LiveAudienceTracker />
          <AppRouter />
          <CommandPalette />
          <OracleAlertsHost />
          <Toaster position="bottom-right" theme="dark" toastOptions={TOAST_OPTIONS} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
